import { Checkbox, CheckboxGroup } from "@mantine/core";
import { twMerge } from "tailwind-merge";

import { useFilterAnalytics } from "@/hooks/analytics.hooks";
import type {
  ArrayKeys,
  ElementOf,
  FilterParamsHandle,
  ParamsConstraint,
} from "@/hooks/use-filter-params";

const COLORS = [
  { color: "Red", tailwindClass: "bg-red-400" },
  { color: "Orange", tailwindClass: "bg-orange-400" },
  { color: "Yellow", tailwindClass: "bg-yellow-400" },
  { color: "Green", tailwindClass: "bg-green-400" },
  { color: "Blue", tailwindClass: "bg-sky-400" },
  { color: "Purple", tailwindClass: "bg-purple-400" },
] as const;

interface HighlightFilterProps<
  K extends ArrayKeys<F>,
  F extends ParamsConstraint,
> {
  availableColors: string[];
  filterKey: K;
  filters: FilterParamsHandle<F>;
}

export const HighlightFilter = <
  K extends ArrayKeys<F>,
  F extends ParamsConstraint,
>({
  availableColors,
  filterKey,
  filters,
}: HighlightFilterProps<K, F>) => {
  const trackFilter = useFilterAnalytics("Highlight");
  const colors = COLORS.filter(({ color }) =>
    availableColors.includes(color.toLowerCase()),
  );

  const handleChange = (color: string) => {
    trackFilter(color);
    filters.toggleValue(filterKey, color as ElementOf<F[K]>);
  };

  return (
    <CheckboxGroup>
      {colors.map(({ color, tailwindClass }) => {
        const lowerCaseColor = color.toLowerCase();
        return (
          <Checkbox
            checked={filters.has(filterKey, lowerCaseColor as ElementOf<F[K]>)}
            className="my-1"
            key={color}
            label={
              <div className="flex items-center">
                <div
                  className={twMerge(
                    "h-2.5",
                    "w-2.5",
                    "flex",
                    "items-center",
                    "rounded-full",
                    "mr-1",
                    tailwindClass,
                  )}
                />
                <div>{color}</div>
              </div>
            }
            onChange={() => handleChange(lowerCaseColor)}
            size="sm"
            value={lowerCaseColor}
          />
        );
      })}
      <Checkbox
        checked={filters.has(filterKey, "none" as ElementOf<F[K]>)}
        className="my-1"
        key="none"
        label={
          <div className="flex items-center">
            <div
              className={twMerge(
                "h-2.5",
                "w-2.5",
                "flex",
                "items-center",
                "rounded-full",
                "mr-1",
                "bg-white",
                "border",
                "border-gray-300",
              )}
            />
            <div>Not highlighted</div>
          </div>
        }
        onChange={() => handleChange("none")}
        size="sm"
        value=""
      />
    </CheckboxGroup>
  );
};
